// Thin client for the backend API (SPEC §8.1 vocabulary: meeting/attendee).
// Requests go same-origin (vite dev proxy / prod reverse proxy) with cookies.

export interface Me {
  id: string;
  name: string;
  email: string | null;
  avatarUrl: string | null;
  /** Platform-admin (F-PLAT-6 dashboard access); config email allowlist. */
  isAdmin: boolean;
  /** Org calling switch (Admin → Settings → Audio). False hides every entry
   * point; the server refuses new calls either way. */
  callsEnabled: boolean;
}

/** One row of the org directory + live presence (People page, Layer B1). */
export interface DirectoryEntry {
  id: string;
  name: string;
  /** Profile photo URL — the same one the meeting room shows. */
  avatar?: string;
  email?: string;
  status: 'online' | 'in_call' | 'dnd' | 'offline';
  self?: boolean;
}

/** A direct 1:1 call (Layer B). Metadata only — never a token or a key. */
/** One person a call rings, with their own outcome (group calls, Layer B5). */
export interface CallPeer {
  id: string;
  name: string;
  avatar?: string;
  state: 'ringing' | 'active' | 'declined' | 'missed' | 'completed';
}
export interface CallInfo {
  id: string;
  slug: string;
  kind: 'direct' | 'group';
  callerId: string;
  callerName: string;
  /** The caller's photo, resolved when the call was placed. */
  callerAvatar?: string;
  /** Direct calls only — a group has no single callee. */
  calleeId: string;
  calleeName: string;
  /** Everyone rung. Exactly one for a direct call. */
  peers: CallPeer[];
  media: 'audio' | 'video';
  state: 'ringing' | 'active' | 'declined' | 'canceled' | 'missed' | 'completed';
  createdAt: string;
}

/** One row of your call list (Layer B4), already resolved to YOUR viewpoint. */
export interface CallHistoryEntry {
  id: string;
  direction: 'in' | 'out';
  peerId: string;
  peerName: string;
  /** Their photo, resolved when the history was read (never stored per call). */
  peerAvatar?: string;
  /** A group call — `peers` holds everyone on it but you. */
  group?: boolean;
  peers?: CallPeer[];
  media: 'audio' | 'video';
  state: string;
  /** True only for an INCOMING call you never answered. */
  missed: boolean;
  startedAt: string;
  answeredAt?: string;
  endedAt?: string;
  durationSec: number;
}

export interface AttendeeVisit {
  name: string;
  identity: string;
  joinedAt: string;
  leftAt: string | null;
}

export interface MeetingHistoryEntry {
  slug: string;
  title: string | null;
  e2eeModel: string;
  createdAt: string;
  endedAt: string | null;
  sessions: number;
  durationSeconds: number;
  attendees: AttendeeVisit[];
  /** Optional (populated once recording/file tracking is wired): a REC badge and
   * a shared-file count badge in the history row. */
  recordingAvailable?: boolean;
  fileCount?: number;
}

export interface AdminOverview {
  activeMeetings: number;
  totalMeetings: number;
  totalSessions: number;
  distinctUsers: number;
  tokensLast24h: number;
  admissionsLast24h: number;
  /** Calls (Layer B): total ever placed, and how many are connected now. */
  totalCalls: number;
  activeCalls: number;
}

export interface LiveMeeting {
  slug: string;
  title: string | null;
  hostName: string;
  e2eeModel: string;
  startedAt: string;
  attendeeCount: number;
}

export interface AdminMeetingHistoryEntry extends MeetingHistoryEntry {
  hostName: string;
}

export interface UsageDay {
  date: string;
  meetings: number;
  avgDurationSec: number;
  attendees: number;
  /** Calls started that day. */
  calls: number;
}
export interface Usage {
  peakConcurrency: number;
  avgDurationSec: number;
  avgAttendeesPerMeeting: number;
  meetingsInWindow: number;
  callsInWindow: number;
  daily: UsageDay[];
}

export interface ComponentHealth {
  name: string;
  ok: boolean;
  error?: string;
}

export interface AuditEvent {
  kind: 'token' | 'admission';
  timestamp: string;
  meetingSlug: string;
  identity: string;
  name: string;
  detail: string;
}

export interface MeetingCreated {
  slug: string;
  e2eeModel: string;
  joinPath: string;
}

export interface MeetingState {
  state: 'exists' | 'live' | 'ended' | 'not_found';
  locked: boolean;
  e2eeModel: string;
  title: string | null;
  /** Lobby presence: display names currently in the live session (capped). */
  attendees: string[];
  attendeeCount: number;
}

export interface MeetingSummary {
  slug: string;
  title: string | null;
  e2eeModel: string;
  locked: boolean;
  createdAt: string;
  /** Schedule-lite (F-PLAT-7): informational only, gates nothing. */
  scheduledAt: string | null;
  endsAt: string | null;
}

export interface SfuSeries {
  key: string;
  title: string;
  unit: string;
  points: { t: number; v: number }[];
}
export interface SfuMetrics {
  enabled: boolean;
  series?: SfuSeries[];
}

export interface AdminUser {
  id: string;
  name: string;
  email: string | null;
  isAdmin: boolean;
  createdAt: string;
  lastSeenAt: string | null;
}

export interface OrgSettings {
  defaultE2eeModel: 'a' | 'b';
  allowGuests: boolean;
  allowRecording: boolean;
  allowFileSharing: boolean;
  /** Meeting Tools org policy. */
  allowPolls: boolean;
  allowTimer: boolean;
  maxAttendees: number;
  maxDurationMin: number;
  /** Join-request chime id played on the host's client (client-synthesized). */
  knockSound: string;
  /** Incoming-call ringtone id (a bundled client asset key, not a path). */
  callRingtone: string;
  /** Whole calling module on/off for the org. */
  callsEnabled: boolean;
  /** Audio capture/encoding policy applied by the client at Room construction. */
  audioStereo: boolean;
  audioEchoCancellation: boolean;
  audioNoiseSuppression: boolean;
  audioAutoGain: boolean;
}

// Email tab (admin): delivery config. Secrets are write-only — read back as a
// presence flag + last-4 hint; sent only when the admin types a new value.
export interface EmailSettingsView {
  provider: 'resend' | 'smtp';
  fromName: string;
  fromEmail: string;
  replyTo: string;
  hasResendKey: boolean;
  resendKeyHint: string;
  smtpHost: string;
  smtpPort: number;
  smtpUsername: string;
  hasSmtpPassword: boolean;
  smtpSecurity: 'starttls' | 'tls' | 'none';
  welcomeEnabled: boolean;
  inviteEnabled: boolean;
  inviteCap: number;
  rateWindowSec: number;
}
export interface EmailSettingsResponse {
  settings: EmailSettingsView;
  /** Live enablement (reflects env fallback, not just the stored row). */
  enabled: boolean;
}
export interface EmailSettingsUpdate {
  provider: string;
  fromName: string;
  fromEmail: string;
  replyTo: string;
  /** Omit to keep the stored key; a value replaces it. */
  resendApiKey?: string;
  smtpHost: string;
  smtpPort: number;
  smtpUsername: string;
  smtpPassword?: string;
  smtpSecurity: string;
  welcomeEnabled: boolean;
  inviteEnabled: boolean;
  inviteCap: number;
  rateWindowSec: number;
}

// Authentication tab (admin): enterprise directory (LDAP/AD) config, proxied to
// Casdoor by the backend. Bind password is write-only — blank when read back.
export interface LdapConfig {
  id: string;
  serverName: string;
  host: string;
  port: number;
  enableSsl: boolean;
  username: string; // bind DN, e.g. cn=admin,dc=corp,dc=com
  password?: string; // write-only; leave blank on edit to keep the stored one
  baseDn: string;
  filter: string;
  filterFields?: string[];
  autoSync: number; // minutes between auto-syncs; 0 = manual only
  lastSync?: string;
}
export interface LdapUserPreview {
  uid: string;
  cn: string;
  email: string;
}
export interface LdapTestResult {
  count: number;
  users: LdapUserPreview[];
}
export interface LdapSyncResult {
  imported: number;
  existed: LdapUserPreview[] | null;
  failed: LdapUserPreview[] | null;
}

// SSO provider (OIDC/OAuth) for the Authentication tab. Secret is write-only.
// `enabled` = whether its "Sign in with …" button shows on the login page.
export interface SsoProvider {
  name: string;
  displayName: string;
  type: string; // Google | AzureAD | Okta | OpenID Connect
  clientId: string;
  clientSecret?: string;
  scopes?: string;
  domain?: string;
  issuerUrl?: string; // generic OIDC — Casdoor auto-discovers endpoints from it
  customAuthUrl?: string;
  customTokenUrl?: string;
  customUserInfoUrl?: string;
  enabled: boolean;
}

// Two-step verification (opt-in, org-wide). factors ⊆ {"App","Email","SMS"}.
export interface MfaConfig {
  enabled: boolean;
  factors: string[];
}

// Self-service profile (the signed-in user's own account).
export interface AccountProfile {
  firstName: string;
  lastName: string;
  displayName: string;
  email: string;
  avatar: string;
}

export interface JoinGrant {
  token: string;
  wsUrl: string;
  identity: string;
  role: 'host' | 'attendee';
  sessionId: string;
  /** Org policy (Phase C): hide the host record button when false. */
  allowRecording?: boolean;
  /** Org policy (F-COM-5): hide the file picker when false. */
  allowFileSharing?: boolean;
  /** Meeting Tools org policy: hide the tool when false. */
  allowPolls?: boolean;
  allowTimer?: boolean;
  /** Duration cap (Phase C): unix seconds when the meeting is force-ended
   * (0/absent = no cap), and the cap in minutes. Drives the countdown + end message. */
  endsAt?: number;
  durationCapMin?: number;
  /** TURN fallback relay (F-PLAT): short-lived ICE creds; offered to the SFU as
   * a fallback candidate, never forced. Absent when TURN isn't configured. */
  iceServers?: { urls: string[]; username?: string; credential?: string }[];
  /** Org-wide join-request chime id the host's client plays on a knock. */
  knockSound?: string;
  /** Org audio capture/encoding policy, applied at Room construction. */
  audio?: {
    stereo: boolean;
    echoCancellation: boolean;
    noiseSuppression: boolean;
    autoGainControl: boolean;
  };
  /** E2EE meetings only, delivered with admission (§5.4). Model A carries the
   * server secret; Model B carries only the model — the client holds its own
   * passphrase (zero-knowledge, §5.2). */
  e2ee?: { model: 'a' | 'b'; secret?: string };
}

export interface KnockCreated {
  knockId: string;
  guestId: string;
  autoAdmit: boolean;
}

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
  ) {
    super(message);
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, { credentials: 'same-origin', ...init });
  if (!res.ok) {
    let message = res.statusText;
    try {
      const body = (await res.json()) as { error?: string };
      if (body.error) message = body.error;
    } catch {
      /* non-JSON error body */
    }
    throw new ApiError(res.status, message);
  }
  return (await res.json()) as T;
}

export const api = {
  me: () => request<Me>('/v1/me'),
  /** Org directory with live presence (People page, Layer B1). */
  directory: (q?: string) =>
    request<DirectoryEntry[]>(`/v1/directory${q ? `?q=${encodeURIComponent(q)}` : ''}`),
  logout: () => request<{ ok: boolean }>('/v1/auth/logout', { method: 'POST' }),
  // Self-service profile (name + avatar), written through to Casdoor.
  account: () => request<AccountProfile>('/v1/account'),
  saveAccount: (firstName: string, lastName: string) =>
    request<AccountProfile>('/v1/account', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ firstName, lastName }),
    }),
  uploadAvatar: (file: File) => {
    const fd = new FormData();
    fd.append('avatar', file);
    return request<{ avatar: string }>('/v1/account/avatar', { method: 'POST', body: fd });
  },
  createMeeting: (body?: {
    e2eeModel?: 'a' | 'b';
    title?: string;
    note?: string;
    scheduledAt?: string;
    endsAt?: string;
  }) =>
    request<MeetingCreated>('/v1/meetings', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body ?? {}),
    }),
  listMeetings: () => request<MeetingSummary[]>('/v1/meetings'),
  // F-PLAT-5 meeting history + F-PLAT-6 admin dashboard/audit.
  /** Past meetings. The date range is applied SERVER-side: the list is capped,
   * so filtering in the browser would answer "nothing in March" from a page
   * that only ever held the most recent few. */
  meetingHistory: (range?: { from?: string; to?: string }) => {
    const q = new URLSearchParams();
    if (range?.from) q.set('from', range.from);
    if (range?.to) q.set('to', range.to);
    const qs = q.toString();
    return request<MeetingHistoryEntry[]>(`/v1/history${qs ? `?${qs}` : ''}`);
  },
  adminOverview: () => request<AdminOverview>('/v1/admin/overview'),
  adminLive: () => request<LiveMeeting[]>('/v1/admin/live'),
  adminUsage: (days = 14) => request<Usage>(`/v1/admin/usage?days=${days}`),
  adminSFU: (range = 60) => request<SfuMetrics>(`/v1/admin/metrics/sfu?range=${range}`),
  adminUsers: (q = '') =>
    request<AdminUser[]>(`/v1/admin/users${q ? `?q=${encodeURIComponent(q)}` : ''}`),
  // Promoting to admin carries the acting admin's password for re-auth
  // confirmation (verified against the IdP); demotion needs none.
  adminSetUserRole: (id: string, isAdmin: boolean, password?: string) =>
    request<{ ok: boolean }>(`/v1/admin/users/${id}/admin`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ isAdmin, password }),
    }),
  adminCreateUser: (name: string, email: string, password: string) =>
    request<{ ok: boolean }>('/v1/admin/users', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name, email, password }),
    }),
  adminUpdateUser: (id: string, email: string, name: string, newEmail?: string) =>
    request<{ ok: boolean }>(`/v1/admin/users/${id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, name, newEmail }),
    }),
  adminDeleteUser: (id: string, email: string) =>
    request<{ ok: boolean }>(`/v1/admin/users/${id}?email=${encodeURIComponent(email)}`, {
      method: 'DELETE',
    }),
  adminSettings: () => request<OrgSettings>('/v1/admin/settings'),
  adminSaveSettings: (s: OrgSettings) =>
    request<{ ok: boolean }>('/v1/admin/settings', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(s),
    }),
  adminEmail: () => request<EmailSettingsResponse>('/v1/admin/email'),
  adminSaveEmail: (u: EmailSettingsUpdate) =>
    request<{ ok: boolean }>('/v1/admin/email', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(u),
    }),
  adminTestEmail: (to?: string) =>
    request<{ ok: boolean; to: string }>('/v1/admin/email/test', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ to: to ?? '' }),
    }),
  // Authentication tab: enterprise directory (LDAP/AD), proxied to Casdoor.
  adminLdaps: () => request<LdapConfig[]>('/v1/admin/auth/ldap'),
  adminSaveLdap: (l: LdapConfig) =>
    request<{ id: string }>('/v1/admin/auth/ldap', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(l),
    }),
  adminTestLdap: (id: string) =>
    request<LdapTestResult>('/v1/admin/auth/ldap/test', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id }),
    }),
  adminSyncLdap: (id: string) =>
    request<LdapSyncResult>('/v1/admin/auth/ldap/sync', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id }),
    }),
  adminDeleteLdap: (id: string) =>
    request<{ ok: boolean }>('/v1/admin/auth/ldap/delete', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id }),
    }),
  // SSO providers (OIDC/OAuth), proxied to Casdoor.
  adminProviders: () => request<SsoProvider[]>('/v1/admin/auth/providers'),
  adminSaveProvider: (p: SsoProvider) =>
    request<{ name: string }>('/v1/admin/auth/providers', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(p),
    }),
  adminDeleteProvider: (name: string) =>
    request<{ ok: boolean }>('/v1/admin/auth/providers/delete', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: name }),
    }),
  // Two-step verification (opt-in), org-wide via Casdoor.
  adminMfa: () => request<MfaConfig>('/v1/admin/auth/mfa'),
  adminSaveMfa: (c: MfaConfig) =>
    request<{ ok: boolean }>('/v1/admin/auth/mfa', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(c),
    }),
  adminHistory: () => request<AdminMeetingHistoryEntry[]>('/v1/admin/history'),
  adminHealth: () => request<ComponentHealth[]>('/v1/admin/health'),
  adminEndMeeting: (slug: string) =>
    request<{ ok: boolean }>(`/v1/admin/meetings/${slug}/end`, { method: 'POST' }),
  adminAudit: (filter: { meeting?: string; identity?: string }) => {
    const q = new URLSearchParams();
    if (filter.meeting) q.set('meeting', filter.meeting);
    if (filter.identity) q.set('identity', filter.identity);
    const qs = q.toString();
    return request<AuditEvent[]>(`/v1/admin/audit${qs ? `?${qs}` : ''}`);
  },
  meetingState: (slug: string) => request<MeetingState>(`/v1/meetings/${slug}`),

  // ── Calls (Layer B). Accept IS the admission: the grant (token + Model-A key)
  // is minted server-side at accept, never handed out before. One peer is a
  // direct call, several is a group call — the server decides from the list.
  startCall: (peers: { id: string; name: string }[], media: 'audio' | 'video' = 'audio') =>
    request<{ call: CallInfo; grant: JoinGrant; joinPath: string }>('/v1/calls', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ peers, media }),
    }),
  /** Your recent calls (Layer B4). */
  callHistory: (limit = 50) => request<CallHistoryEntry[]>(`/v1/calls?limit=${limit}`),
  /** Re-mint MY grant for a live call (§5.5 reconnect analog) — idempotent. */
  joinCall: (id: string) =>
    request<{ call: CallInfo; grant: JoinGrant; joinPath: string }>(`/v1/calls/${id}/join`, {
      method: 'POST',
    }),
  acceptCall: (id: string) =>
    request<{ call: CallInfo; grant: JoinGrant; joinPath: string }>(`/v1/calls/${id}/accept`, {
      method: 'POST',
    }),
  /** Do Not Disturb: durable (it outlives this tab) and broadcast to the org. */
  setDnd: (on: boolean) =>
    request<{ dnd: boolean }>('/v1/presence/dnd', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ on }),
    }),
  /** Ring more people into a call already in progress (the in-call "add
   * someone" button). Grants nothing — each newcomer passes admission at
   * accept, same as any other answer. */
  inviteToCall: (id: string, peers: { id: string; name: string }[]) =>
    request<CallInfo>(`/v1/calls/${id}/invite`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ peers }),
    }),
  declineCall: (id: string) =>
    request<{ ok: boolean }>(`/v1/calls/${id}/decline`, { method: 'POST' }),
  cancelCall: (id: string) => request<{ ok: boolean }>(`/v1/calls/${id}/cancel`, { method: 'POST' }),
  endCall: (id: string) => request<{ ok: boolean }>(`/v1/calls/${id}/end`, { method: 'POST' }),
  getCall: (id: string) => request<CallInfo>(`/v1/calls/${id}`),
  // Email a Join link to attendees (host-only, non-zero-knowledge meetings).
  meetingInvite: (slug: string, emails: string[]) =>
    request<{ sent: number }>(`/v1/meetings/${slug}/invite`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ emails }),
    }),
  patchMeeting: (
    slug: string,
    body: { title?: string; scheduledAt?: string; clearTitle?: boolean; clearSchedule?: boolean },
  ) =>
    request<{ ok: boolean }>(`/v1/meetings/${slug}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    }),
  join: (slug: string) => request<JoinGrant>(`/v1/meetings/${slug}/join`, { method: 'POST' }),
  knock: (slug: string, name: string, guestId?: string) =>
    request<KnockCreated>(`/v1/meetings/${slug}/knock`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name, guestId }),
    }),
  admit: (slug: string, knockId: string) =>
    request<{ ok: boolean }>(`/v1/meetings/${slug}/admit`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ knockId }),
    }),
  deny: (slug: string, knockId: string) =>
    request<{ ok: boolean }>(`/v1/meetings/${slug}/deny`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ knockId }),
    }),
  // Host-only polling backstop for the admission WebSocket (never miss a knock).
  admissionQueue: (slug: string) =>
    request<{ id: string; name: string; at: string }[]>(`/v1/meetings/${slug}/queue`),
  end: (slug: string) => request<{ ok: boolean }>(`/v1/meetings/${slug}/end`, { method: 'POST' }),

  // Host powers (§7.5). All admin-authorized server-side.
  muteAttendee: (slug: string, id: string) => post(`/v1/meetings/${slug}/attendees/${id}/mute`),
  muteAll: (slug: string) => post(`/v1/meetings/${slug}/mute-all`),
  removeAttendee: (slug: string, id: string) => post(`/v1/meetings/${slug}/attendees/${id}/remove`),
  setRole: (slug: string, id: string, role: string) =>
    postJSON(`/v1/meetings/${slug}/attendees/${id}/role`, { role }),
  setPolicy: (slug: string, policy: MeetingPolicy) => postJSON(`/v1/meetings/${slug}/policy`, policy),
  setLock: (slug: string, locked: boolean) => postJSON(`/v1/meetings/${slug}/lock`, { locked }),
  offerTransfer: (slug: string, targetUserId: string) =>
    postJSON(`/v1/meetings/${slug}/transfer`, { targetUserId }),
  acceptTransfer: (slug: string) => post(`/v1/meetings/${slug}/transfer/accept`),

  // Encrypted file sharing presign (F-COM-5): the room token authorizes.
  presignUpload: (token: string, fileId: string, size: number) =>
    request<{ uploadUrl: string; objectKey: string }>('/v1/files/presign', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token, fileId, size }),
    }),
  presignDownload: (token: string, objectKey: string) =>
    request<{ downloadUrl: string }>('/v1/files/presign', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token, objectKey }),
    }),
};

export interface MeetingPolicy {
  allowShare: boolean;
  allowUnmute: boolean;
  allowChat: boolean;
}

export type Role = 'host' | 'cohost' | 'attendee' | 'viewonly';

function post(path: string) {
  return request<{ ok: boolean }>(path, { method: 'POST' });
}
function postJSON(path: string, body: unknown) {
  return request<{ ok: boolean }>(path, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
}

export const loginUrl = '/v1/auth/login';

// Casdoor origin for the current environment. Credentials, signup and account
// management live on the IdP; the app only deep-links there.
export function casdoorBase(): string {
  const { origin } = window.location;
  if (origin.includes('app.brizina.com')) return 'https://id.brizina.com';
  if (origin.includes('localhost:5273')) return 'http://localhost:8000';
  return origin.replace(/\/\/(app|admin)\./, '//id.').replace(':5273', ':8000');
}

// Self-service registration goes through the backend so the OAuth/PKCE context
// rides along: Casdoor shows the branded signup form and, once done, redirects
// back to the app (not the Casdoor portal). Guests never need this — invite
// links admit them without an account.
export const signupUrl = '/v1/auth/register';

/** Model B secret carried in the current page's link fragment (#k=…).
 * Fragments never reach a server (§5.2). Returns null when absent. */
export function readModelBSecret(): string | null {
  const m = /[#&]k=([^&]+)/.exec(window.location.hash);
  return m && m[1] ? decodeURIComponent(m[1]) : null;
}

/** The shareable meeting URL — origin + join path, with the Model B key in the
 * #k= fragment when present. Single source of truth for every "share this
 * meeting" surface (home create, in-call copy link, the meeting-ready card). */
export function meetingShareLink(joinPath: string, passphrase?: string | null): string {
  const frag = passphrase ? `#k=${encodeURIComponent(passphrase)}` : '';
  return `${window.location.origin}${joinPath}${frag}`;
}
